import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Check, FileSpreadsheet, AlertTriangle, Download, ExternalLink, RefreshCw, X, Search, Undo2 } from 'lucide-react'
import {
  importBankStatement, getBankTransactions,
  bulkApproveBankTransactions, downloadBankStatementCsv, publishBankStatementSheet, saveBankBatch,
  getExpenseCategories, getInvoices, allocateBankCredit, undoBankTransaction,
  getInvoice, voidPayment, reverseInvoiceAdjustment,
} from '../services/api'
import { formatCurrency } from '../lib/formatters'
import { useToast } from '../components/Toast'
import { useAuth } from '../contexts/AuthContext'

/**
 * Bank statement → what each line was for.
 *
 * The statement is parsed and stored server-side, so this page reviews saved rows
 * rather than re-parsing a file every time: a half-finished month survives a
 * reload, and re-uploading an overlapping statement changes nothing that has
 * already been approved.
 *
 * Only the lines the matcher could not settle need attention here. Every answer
 * given is remembered, so the same counterparty is never asked about twice.
 */

// Live scope — earlier months were reconciled by hand and are not re-opened.
const SCOPE_START = '2026-04-01'

const STATUS_TABS = [
  { key: 'UNRESOLVED', label: 'Needs you', tone: 'text-amber-700' },
  { key: 'SUGGESTED', label: 'Suggested', tone: 'text-blue-700' },
  { key: 'APPROVED', label: 'Confirmed', tone: 'text-emerald-700' },
  { key: 'IGNORED', label: 'Not accounted', tone: 'text-gray-500' },
]

const todayIso = () => new Date().toISOString().slice(0, 10)

export default function BankReconciliation() {
  const [transactions, setTransactions] = useState([])
  const [counts, setCounts] = useState({})
  // What "confirm all" would actually write — not the same as the number of
  // suggested rows, which is how it got clicked by mistake.
  const [eligible, setEligible] = useState({ n: 0, value: 0 })
  const [categories, setCategories] = useState([])
  const [tab, setTab] = useState('UNRESOLVED')
  const [range, setRange] = useState({ from: SCOPE_START, to: todayIso() })
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState({})
  const [fileName, setFileName] = useState('')
  // Decisions held locally until Save. Nothing is written while reviewing, so a
  // pass costs one request instead of two per row — and the whole set can be
  // read back and corrected before any accounting record exists.
  const [staged, setStaged] = useState({})   // id -> { action, categoryId?, invoiceId? }
  const toast = useToast()
  // Held in a ref so a callback can raise a toast without taking a dependency on
  // it. Even with the provider memoised, keeping `toast` in a dependency array
  // re-arms the refetch-on-every-toast trap for whoever touches this next.
  const toastRef = useRef(toast)
  toastRef.current = toast

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await getBankTransactions({ ...range, status: tab, take: 500 })
      setTransactions(data.transactions)
      setCounts(data.counts || {})
      setEligible({ n: data.autoApproveEligible || 0, value: data.autoApproveValue || 0 })
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not load transactions')
    } finally {
      setLoading(false)
    }
  }, [range, tab])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    getExpenseCategories()
      .then(({ data }) => setCategories(data.filter((c) => c.isActive !== false)))
      .catch(() => toastRef.current.error('Could not load expense categories'))
    // The 1000-invoice fetch that used to live here fed a dropdown of "the 60
    // nearest by amount". That list could not answer 28 of 32 stuck credits, and
    // the panel now searches on demand, so the bulk fetch is gone.
  // Reference data does not change during a review pass — fetch it once.
  }, [])

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setLoading(true)
    try {
      const { data } = await importBankStatement(file, SCOPE_START)
      toastRef.current.success(
        `${data.parsed} lines read — ${data.created} new, ${data.updated} updated, ` +
        `${data.untouched} already confirmed and left alone`,
      )
      await load()
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not read that statement')
    } finally {
      setLoading(false)
      e.target.value = '' // let the same file be re-selected
    }
  }, [load])

  const withBusy = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }))
    try { await fn() } finally { setBusy((b) => ({ ...b, [id]: false })) }
  }

  // Staging only — no request. The row stays visible so the whole pass can be
  // read back before anything is written.
  const stage = (txn, decision) => setStaged((s) => ({ ...s, [txn.id]: { ...s[txn.id], ...decision } }))
  const unstage = (txn) => setStaged((s) => { const next = { ...s }; delete next[txn.id]; return next })

  const stagedCount = Object.keys(staged).length

  const handleSave = () => withBusy('save', async () => {
    const items = Object.entries(staged).map(([id, d]) => ({ id, ...d }))
    if (!items.length) return
    try {
      const { data } = await saveBankBatch(items)
      const done = data.saved + data.skipped + data.alreadyPosted
      if (data.failed) {
        toastRef.current.error(`${done} saved, ${data.failed} could not be — those stay in the list`)
        // eslint-disable-next-line no-console
        console.warn('Batch failures:', data.errors)
      } else {
        toastRef.current.success(`${done} saved`)
      }
      // Drop only what actually saved; anything that failed stays staged so it
      // can be corrected rather than silently lost.
      const savedIds = new Set(data.savedIds || [])
      setTransactions((rows) => rows.filter((r) => !savedIds.has(r.id)))
      setStaged((s) => Object.fromEntries(Object.entries(s).filter(([id]) => !savedIds.has(id))))
      if (data.counts) setCounts(data.counts)
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not save')
    }
  })


  const handleBulkApprove = () => withBusy('bulk', async () => {
    // It writes payments and expenses. Say what and how much, then ask.
    if (!window.confirm(
      `Record ${eligible.n} high-confidence ${eligible.n === 1 ? 'entry' : 'entries'} `
      + `totalling ${formatCurrency(eligible.value)}?\n\n`
      + `Lower-confidence suggestions are not touched — review those individually.`,
    )) return
    try {
      const { data } = await bulkApproveBankTransactions({ ...range })
      if (data.failed) {
        toastRef.current.error(`${data.approved} recorded, ${data.failed} could not be — see the list`)
        // eslint-disable-next-line no-console
        console.warn('Bulk approve failures:', data.errors)
      } else {
        toastRef.current.success(`${data.approved} entries recorded`)
      }
      await load()
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Bulk approve failed')
    }
  })

  const handleDownloadCsv = () => withBusy('csv', async () => {
    try {
      const { data } = await downloadBankStatementCsv(range)
      const url = URL.createObjectURL(data)
      const a = document.createElement('a')
      a.href = url
      a.download = `bank-statement-annotated-${range.from}-to-${range.to}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toastRef.current.error('Could not build the CSV')
    }
  })

  const handlePublishSheet = () => withBusy('sheet', async () => {
    try {
      const { data } = await publishBankStatementSheet(range)
      toastRef.current.success(
        data.unresolved
          ? `Sheet published — but ${data.unresolved} lines still say NEEDS REVIEW`
          : 'Sheet published to the Books Drive folder',
      )
      if (data.webViewLink) window.open(data.webViewLink, '_blank', 'noopener')
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not publish the sheet')
    }
  })

  const totals = useMemo(() => ({
    withdrawal: transactions.reduce((s, t) => s + Number(t.withdrawal || 0), 0),
    deposit: transactions.reduce((s, t) => s + Number(t.deposit || 0), 0),
  }), [transactions])

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Bank Reconciliation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every line of the ICICI statement, with what it was for — for the CA.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadCsv} disabled={busy.csv} className="btn-secondary btn-sm flex items-center gap-1.5">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button onClick={handlePublishSheet} disabled={busy.sheet} className="btn-secondary btn-sm flex items-center gap-1.5">
            <ExternalLink className="h-3.5 w-3.5" /> {busy.sheet ? 'Publishing…' : 'Publish Sheet'}
          </button>
        </div>
      </div>

      {/* Upload + period */}
      <div className="card space-y-4">
        <label className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-brand-400 hover:bg-brand-50/30 transition-colors">
          <FileSpreadsheet className="h-9 w-9 text-gray-300 mb-2" />
          <span className="text-sm font-medium text-gray-600">
            {fileName || 'Drop the ICICI statement (.xlsx) here, or click to browse'}
          </span>
          <span className="text-xs text-gray-400 mt-1">
            Re-uploading an overlapping statement is safe — confirmed lines are never touched
          </span>
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
        </label>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={range.from} min={SCOPE_START}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="input" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="input" />
          </div>
          <button onClick={load} disabled={loading} className="btn-secondary btn-sm flex items-center gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <div className="flex-1" />
          <button onClick={handleBulkApprove} disabled={busy.bulk || !eligible.n}
            title={eligible.n
              ? `Only suggestions at or above the confidence floor are written. ${(counts.SUGGESTED || 0) - eligible.n} lower-confidence rows stay for review.`
              : 'Nothing is confident enough to record in bulk'}
            className={`btn-sm flex items-center gap-1.5 ${stagedCount > 0 ? 'btn-secondary' : 'btn-primary'}`}>
            <Check className="h-3.5 w-3.5" />
            {busy.bulk ? 'Recording…' : `Confirm ${eligible.n} high-confidence`}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        {STATUS_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? `border-brand-600 ${t.tone}` : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t.label} {counts[t.key] != null && <span className="text-xs text-gray-400">({counts[t.key]})</span>}
          </button>
        ))}
      </div>

      {loading && (
        <div className="card text-center py-10">
          <div className="animate-spin h-8 w-8 border-2 border-brand-600 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      )}

      {!loading && !transactions.length && (
        <div className="card text-center py-12">
          <Check className="h-9 w-9 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">Nothing here for this period.</p>
        </div>
      )}

      {stagedCount > 0 && (
        <div className="sticky bottom-0 z-40 -mx-1 px-5 py-3 bg-white border-t shadow-lg flex items-center justify-between gap-4 rounded-b-xl">
          <span className="text-sm text-gray-700">
            <span className="font-semibold">{stagedCount}</span> decision{stagedCount === 1 ? '' : 's'} staged — nothing written yet
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setStaged({})} disabled={busy.save} className="btn-secondary btn-sm">
              Discard
            </button>
            <button onClick={handleSave} disabled={busy.save} className="btn-primary btn-sm flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              {busy.save ? 'Saving…' : `Save ${stagedCount} change${stagedCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {!loading && transactions.length > 0 && (
        <>
          <div className="flex items-center justify-between text-xs text-gray-500 px-1">
            <span>{transactions.length} lines</span>
            <span>
              Out {formatCurrency(totals.withdrawal)} · In {formatCurrency(totals.deposit)}
            </span>
          </div>

          <div className="card !p-0 overflow-hidden divide-y">
            {transactions.map((txn) => (
              <TransactionRow
                key={txn.id}
                txn={txn}
                categories={categories}
                staged={staged[txn.id]}
                onStage={(decision) => stage(txn, decision)}
                onUnstage={() => unstage(txn)}
                onAllocated={(row) => {
                  // Allocation writes immediately, so drop the row and adjust the
                  // tab counts locally rather than refetching the whole list.
                  setTransactions((rows) => rows.filter((r) => r.id !== row.id))
                  setCounts((c) => ({ ...c, [tab]: Math.max(0, (c[tab] || 1) - 1), APPROVED: (c.APPROVED || 0) + 1 }))
                }}
                onUndone={(row) => {
                  // Undo writes immediately too — mirror onAllocated: drop the
                  // row locally rather than refetching 500 rows mid-review.
                  setTransactions((rows) => rows.filter((r) => r.id !== row.id))
                  setCounts((c) => ({
                    ...c,
                    [tab]: Math.max(0, (c[tab] || 1) - 1),
                    UNRESOLVED: (c.UNRESOLVED || 0) + 1,
                  }))
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function TransactionRow({ txn, categories, staged, onStage, onUnstage, onAllocated, onUndone }) {
  const [allocating, setAllocating] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const { isAdmin } = useAuth()
  const toast = useToast()
  const isDebit = txn.direction === 'DEBIT'
  const amount = Number(isDebit ? txn.withdrawal : txn.deposit)
  const categoryId = staged?.categoryId ?? txn.suggestedCategoryId ?? ''
  const invoiceId = staged?.invoiceId ?? txn.suggestedInvoiceId ?? ''
  const posted = txn.status === 'APPROVED'
  const isStaged = Boolean(staged)

  // Reverses what approving this line did. The server keeps a payment that
  // existed before the line was reviewed ("already reconciled") and only
  // removes the link — the message stays honest about that.
  const handleUndo = async () => {
    const msg = `Undo this recorded credit of ${formatCurrency(amount)}?`
      + (txn.resolvedNote ? `\n\nRecorded against: ${txn.resolvedNote}` : '')
      + '\n\nAny payments this line created are deleted and those invoices owe the money again. A payment that was recorded before this line was reviewed is kept — only the link is removed. The line goes back to "Needs you".'
    if (!window.confirm(msg)) return
    setUndoing(true)
    try {
      const { data } = await undoBankTransaction(txn.id)
      toast.success(data.reversed?.length
        ? `Undone — ${data.reversed.length} payment${data.reversed.length === 1 ? '' : 's'} removed`
        : 'Undone — the line is back under review; no payment was deleted')
      onUndone?.(txn)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to undo')
    } finally {
      setUndoing(false)
    }
  }


  const canApprove = isDebit ? Boolean(categoryId) : Boolean(invoiceId)

  return (
    <div className={`px-5 py-4 transition-colors ${
      posted ? 'bg-emerald-50/40' : isStaged ? 'bg-blue-50/50' : 'hover:bg-gray-50/40'
    }`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-gray-500">
              {new Date(txn.transactionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDebit ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
              {isDebit ? 'Paid out' : 'Received'}
            </span>
            {txn.confidence > 0 && !posted && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                {txn.confidence}% confident
              </span>
            )}
            {isStaged && (
              <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-medium">
                {staged.action === 'skip' ? 'staged: not accounted' : 'staged'}
              </span>
            )}
            {txn.status === 'UNRESOLVED' && !isStaged && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                <AlertTriangle className="h-3 w-3" /> Needs you
              </span>
            )}
          </div>
          <p className="text-sm text-gray-800 break-words">{txn.remarks}</p>
          {txn.matchReason && (
            <p className="text-xs text-gray-400 mt-0.5">{txn.matchReason}</p>
          )}
        </div>

        <div className="text-right w-28 flex-shrink-0">
          <p className={`text-lg font-bold ${isDebit ? 'text-rose-600' : 'text-emerald-600'}`}>
            {formatCurrency(amount)}
          </p>
        </div>

        <div className="w-64 flex-shrink-0">
          {isDebit ? (
            <select value={categoryId} disabled={posted}
              onChange={(e) => onStage({ action: 'approve', categoryId: e.target.value || null })}
              className="select w-full">
              <option value="">— what was this for? —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : (
            <div className="space-y-1">
              {/* Only the invoice the matcher actually proposed is offered as a
                  one-click option. The old list of "60 nearest by amount" was
                  noise: for 28 of 32 stuck credits no invoice matches the amount
                  at all, because they are bulk settlements. */}
              {txn.suggestedInvoice && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={Boolean(invoiceId)} disabled={posted}
                    onChange={() => onStage({ action: 'approve', invoiceId: txn.suggestedInvoiceId })} />
                  <span className="truncate">
                    {txn.suggestedInvoice.number} · {txn.suggestedInvoice.customer?.name}
                  </span>
                </label>
              )}
              {!posted && (
                <button onClick={() => setAllocating((v) => !v)}
                  className="btn-secondary btn-sm w-full flex items-center justify-center gap-1.5">
                  <Search className="h-3.5 w-3.5" />
                  {allocating ? 'Close' : txn.suggestedInvoice ? 'Find another / split' : 'Find invoices'}
                </button>
              )}
            </div>
          )}
          {txn.resolvedNote && posted && (
            <p className="text-xs text-emerald-700 mt-1">{txn.resolvedNote}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {posted ? (
            <>
              <span className="flex items-center gap-1 text-sm text-emerald-600"><Check className="h-4 w-4" /> Recorded</span>
              {/* Credits only — an expense reversal is a different operation —
                  and admin only: it deletes financial records. */}
              {isAdmin && !isDebit && (
                <button
                  disabled={undoing}
                  className="btn-secondary btn-sm"
                  title="Reverse this recorded credit"
                  onClick={handleUndo}
                >
                  {undoing ? 'Undoing…' : 'Undo'}
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={onUnstage} disabled={!isStaged}
                className="btn-secondary btn-sm" title="Undo this staged decision">
                Undo
              </button>
              <button onClick={() => onStage({ action: 'skip' })}
                className="btn-secondary btn-sm" title="Not an expense or a customer payment">
                Skip
              </button>
              <button onClick={() => onStage({ action: 'approve', ...(isDebit ? { categoryId } : { invoiceId }) })}
                disabled={!canApprove} className="btn-primary btn-sm flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> {isStaged && staged.action === 'approve' ? 'Staged' : 'Confirm'}
              </button>
            </>
          )}
        </div>
      </div>

      {allocating && !posted && (
        <AllocationPanel txn={txn} onCancel={() => setAllocating(false)}
          onDone={() => { setAllocating(false); onAllocated?.(txn) }} />
      )}
    </div>
  )
}

/**
 * Find and allocate invoices for one credit.
 *
 * Replaces a dropdown that offered the 60 invoices closest in AMOUNT to the
 * credit. That could not work: 28 of the 32 stuck credits have no invoice at
 * their amount at all, because they are bulk settlements — one payment clearing
 * several invoices. Four different credits were being offered the same unrelated
 * invoice simply because it was nearest by amount.
 *
 * So: search by customer or invoice number, tick several, set an amount on each.
 */
/**
 * What to do about a credit whose invoice is already settled.
 *
 * Two honest answers, and the difference is whether Books already holds this
 * money:
 *  - ALREADY IN BOOKS — the receipt was recorded by hand, or as part of a
 *    larger payment. The credit is attributed to the invoice for the CA and no
 *    second payment is created, because that would show the customer as having
 *    overpaid.
 *  - REOPEN — whatever closed the invoice was wrong. A payment recorded at the
 *    wrong amount, or a round-off that closed a balance the customer then
 *    actually paid. Reverse that entry, the invoice owes again, and the credit
 *    posts as an ordinary payment.
 *
 * The live case for the second: invoice O3D-2627-0027 is ₹29,990, ₹10 arrived
 * on 16 Apr and ₹29,980 followed. Whichever way Books recorded that, the ₹10
 * statement line had nowhere to go.
 */
function SettledDisposition({ line, onChoose, onReopened }) {
  const [open, setOpen] = useState(false)
  const annotate = line.annotateOnly !== false

  return (
    <div className="pl-7 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500">This invoice is already paid —</span>
        <div className="inline-flex rounded-md border overflow-hidden">
          <button
            onClick={() => { onChoose(true); setOpen(false) }}
            title="No payment is created — the money is already recorded in Books"
            className={`px-2.5 py-1 ${annotate ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            Already in Books
          </button>
          {/* Opening the panel is not itself a decision: the line stays an
              annotation until something is actually reversed, and onReopened
              flips it. Flipping here would send a posting line against a zero
              balance, and the server 400s the WHOLE allocation — taking the
              correct sibling lines down with it. */}
          <button
            onClick={() => setOpen((v) => !v)}
            className={`px-2.5 py-1 border-l ${open ? 'bg-amber-50 text-amber-800 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            Reopen…
          </button>
        </div>
      </div>

      {annotate && !open && (
        <p className="text-xs text-gray-400">
          The credit is recorded against {line.invoice.number} for the statement. No payment is created.
        </p>
      )}

      {open && (
        <ReopenInvoicePanel invoiceId={line.invoice.id} onReopened={(fresh) => { onReopened(fresh); setOpen(false) }} />
      )}
    </div>
  )
}

/**
 * Reverse whatever closed an invoice, so a credit can be posted against it.
 *
 * Admin only — both actions delete or undo a financial record. A non-admin sees
 * what is on the invoice and who to ask.
 */
function ReopenInvoicePanel({ invoiceId, onReopened }) {
  const [inv, setInv] = useState(null)
  const [busy, setBusy] = useState('')
  // { kind, row } — the entry about to be reversed, while its reason is typed.
  const [confirming, setConfirming] = useState(null)
  const [reason, setReason] = useState('')
  const { isAdmin } = useAuth()
  const toast = useToast()

  const [loadError, setLoadError] = useState('')

  const load = useCallback(() => {
    getInvoice(invoiceId)
      .then(({ data }) => { setInv(data); setLoadError('') })
      // STAFF and INVOICE_CREATOR only see invoices they raised, so this 403s
      // for them. Say so rather than spinning forever.
      .catch((err) => setLoadError(err.response?.data?.error || 'Could not load this invoice'))
  }, [invoiceId])

  useEffect(() => { load() }, [load])

  const ask = (kind, row) => { setReason(''); setConfirming({ kind, row }) }

  const run = async () => {
    const { kind, row } = confirming
    setBusy(row.id)
    try {
      if (kind === 'payment') await voidPayment(row.id, { reason })
      else await reverseInvoiceAdjustment(invoiceId, row.id, { reason })
      // Hand back the invoice as it now stands, so the allocation line picks up
      // the balance that exists rather than the zero it was picked with.
      const { data } = await getInvoice(invoiceId)
      setInv(data)
      setConfirming(null)
      toast.success(`${data.number} now owes ${formatCurrency(data.balanceDue)}`)
      if (Number(data.balanceDue) > 0) onReopened(data)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not reopen that invoice')
    } finally {
      setBusy('')
    }
  }

  if (loadError) return <p className="text-xs text-amber-800">{loadError}</p>
  if (!inv) return <p className="text-xs text-gray-400">Loading{'\u2026'}</p>

  const adjustments = (inv.adjustments || []).filter((a) => !a.reversedAt)

  return (
    <div className="border rounded-md bg-amber-50/40 p-2.5 space-y-2">
      <p className="text-xs text-gray-600">
        What closed {inv.number}. Reverse the entry that was wrong, then allocate the credit normally.
      </p>

      {!isAdmin && (
        <p className="text-xs text-amber-800">Ask an admin to reopen this invoice.</p>
      )}

      {!inv.payments?.length && !adjustments.length && (
        <p className="text-xs text-gray-500">
          Nothing on this invoice can be reversed — its balance was not closed by a payment or a round off.
        </p>
      )}

      <div className="divide-y">
        {inv.payments?.map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-1.5 text-xs">
            <span className="text-gray-500 w-20 shrink-0">{new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' })}</span>
            <span className="font-medium">{formatCurrency(p.amount)}</span>
            <span className="text-gray-500 truncate flex-1">{p.method?.replace(/_/g, ' ')}{p.referenceNo ? ` · ${p.referenceNo}` : ''}</span>
            {isAdmin && (
              <button disabled={busy === p.id} onClick={() => ask('payment', p)}
                className="text-gray-400 hover:text-rose-600 shrink-0">
                {busy === p.id ? 'Voiding…' : 'Void'}
              </button>
            )}
          </div>
        ))}
        {adjustments.map((a) => (
          <div key={a.id} className="flex items-center gap-2 py-1.5 text-xs">
            <span className="text-gray-500 w-20 shrink-0">{new Date(a.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' })}</span>
            <span className="font-medium text-amber-700">{formatCurrency(a.amount)}</span>
            <span className="text-gray-500 truncate flex-1">round off{a.note ? ` · ${a.note}` : ''}</span>
            {isAdmin && (
              <button disabled={busy === a.id} onClick={() => ask('adjustment', a)}
                className="text-gray-400 hover:text-amber-700 shrink-0 flex items-center gap-1">
                <Undo2 className="h-3 w-3" />{busy === a.id ? 'Reversing…' : 'Reverse'}
              </button>
            )}
          </div>
        ))}
      </div>

      {confirming && (
        <div className="border-t pt-2 space-y-2">
          <p className="text-xs text-gray-700">
            {confirming.kind === 'payment'
              ? `Voiding ${formatCurrency(confirming.row.amount)} — the payment record is deleted and any bank line it came from goes back to "Needs you".`
              : `Reversing ${formatCurrency(confirming.row.amount)} — the invoice owes that amount again.`}
          </p>
          <input autoFocus className="input text-xs py-1" placeholder="Why? — recorded against the invoice"
            value={reason} onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && reason.trim() && !busy) run() }} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirming(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={run} disabled={!reason.trim() || Boolean(busy)} className="btn-danger btn-sm">
              {busy ? 'Working…' : confirming.kind === 'payment' ? 'Void payment' : 'Reverse round off'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function AllocationPanel({ txn, onDone, onCancel }) {
  const credit = Number(txn.deposit)
  // Seed the search with the customer the matcher identified, when it did — it
  // saves typing on the 8 rows where a customer is known.
  const [query, setQuery] = useState(txn.suggestedInvoice?.customer?.name || '')
  const [results, setResults] = useState([])
  const [truncated, setTruncated] = useState(0)
  const [searching, setSearching] = useState(false)
  const [picked, setPicked] = useState({})      // invoiceId -> { invoice, amount }
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    // Clearing the box must also clear "Searching…", or it sticks forever and
    // hides the empty state.
    if (query.trim().length < 2) { setResults([]); setTruncated(0); setSearching(false); return undefined }
    setSearching(true)
    // A slow earlier response must not overwrite a newer one, or the list ends up
    // disagreeing with what is typed in the box.
    let current = true
    const timer = setTimeout(() => {
      // 40 was too few and cut the wrong end: results come back newest-first while
      // the panel's default is to tick oldest-first, and Balaji alone has 63.
      getInvoices({ search: query.trim(), take: 200 })
        .then(({ data }) => {
          if (!current) return
          setResults(data.invoices || [])
          setTruncated(Math.max(0, (data.total || 0) - (data.invoices || []).length))
        })
        .catch(() => { if (current) { setResults([]); setTruncated(0) } })
        .finally(() => { if (current) setSearching(false) })
    }, 300)
    return () => { current = false; clearTimeout(timer) }
  }, [query])

  const allocated = Object.values(picked).reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
  const remaining = credit - allocated
  const overAllocated = remaining < -0.01
  const count = Object.keys(picked).length
  // Nothing is posted when every picked invoice is only being annotated, so
  // the button must not promise a payment.
  const allAnnotate = count > 0 && Object.values(picked).every((p) => p.annotateOnly)

  const toggle = (invoice) => setPicked((current) => {
    if (current[invoice.id]) {
      const next = { ...current }; delete next[invoice.id]; return next
    }
    // Default each line to whichever is smaller: what the invoice still owes, or
    // what is left of the credit. That makes oldest-first ticking add up.
    //
    // A settled invoice owes nothing, so that default is 0.00 and the line
    // would be silently dropped by handleSave. It is picked for a different
    // reason — the money is already in Books and the credit needs attributing
    // to it — so the ceiling is the invoice total and the line defaults to
    // annotate.
    const settled = Number(invoice.balanceDue) <= 0
    const ceiling = settled ? Number(invoice.total) : Number(invoice.balanceDue)
    const left = credit - Object.values(current).reduce((s, p) => s + (Number(p.amount) || 0), 0)
    return {
      ...current,
      [invoice.id]: {
        invoice,
        annotateOnly: settled,
        amount: Math.max(0, Math.min(ceiling, left)).toFixed(2),
      },
    }
  })

  // Replaces a picked invoice with its freshly-reopened self, so the amount and
  // the disposition follow the balance that now exists. Without this the stale
  // balanceDue of 0 stays in `picked` and the allocation is rejected server-side.
  const reopened = (fresh) => setPicked((current) => {
    const line = current[fresh.id]
    if (!line) return current
    const left = credit - Object.values(current)
      .reduce((s, p) => s + (p.invoice.id === fresh.id ? 0 : Number(p.amount) || 0), 0)
    return {
      ...current,
      [fresh.id]: {
        invoice: fresh,
        annotateOnly: Number(fresh.balanceDue) <= 0,
        amount: Math.max(0, Math.min(Number(fresh.balanceDue), left)).toFixed(2),
      },
    }
  })

  const setDisposition = (id, annotateOnly) => setPicked((c) => ({ ...c, [id]: { ...c[id], annotateOnly } }))

  const setAmount = (id, value) => setPicked((c) => ({ ...c, [id]: { ...c[id], amount: value } }))

  const handleSave = async () => {
    setSaving(true)
    try {
      // A line left at 0.00 (ticked once the credit was fully used) would make the
      // backend reject the entire allocation, losing the correct lines with it.
      const allocations = Object.entries(picked)
        .map(([invoiceId, p]) => ({ invoiceId, amount: Number(p.amount), annotateOnly: Boolean(p.annotateOnly) }))
        .filter((a) => a.amount > 0)
      if (!allocations.length) { toast.error('Set an amount on at least one invoice'); setSaving(false); return }
      const { data } = await allocateBankCredit(txn.id, allocations)
      if (data.alreadyPosted) toast.info('That credit was already recorded')
      else {
        // An all-annotate allocation records no payment at all, so saying
        // "0 payments recorded" would read as a failure.
        const posted = data.payments.length
        const linked = data.annotated?.length || 0
        toast.success(
          [
            posted ? `${posted} payment${posted === 1 ? '' : 's'} recorded` : null,
            linked ? `${linked} invoice${linked === 1 ? '' : 's'} linked (already in Books)` : null,
          ].filter(Boolean).join(', ')
          + (data.unallocated > 0 ? ` — ${formatCurrency(data.unallocated)} left unallocated` : ''),
        )
      }
      onDone()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not allocate')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-3 border rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">
          <span className="text-gray-500">Allocating</span>{' '}
          <span className="font-semibold text-emerald-700">{formatCurrency(credit)}</span>
        </div>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>

      <input autoFocus className="input" placeholder="Search by customer or invoice number…"
        value={query} onChange={(e) => setQuery(e.target.value)} />

      {picked && Object.keys(picked).length > 0 && (
        <div className="border rounded-md divide-y">
          {Object.entries(picked).map(([id, p]) => {
            const settled = Number(p.invoice.balanceDue) <= 0
            return (
              <div key={id} className="px-3 py-2 text-sm space-y-2">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggle(p.invoice)} className="text-gray-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{p.invoice.number}</span>
                    <span className="text-gray-500"> · {p.invoice.customer?.name}</span>
                    <span className="text-gray-400">
                      {settled ? ` · paid, of ${formatCurrency(p.invoice.total)}` : ` · owes ${formatCurrency(p.invoice.balanceDue)}`}
                    </span>
                  </div>
                  <input type="number" step="0.01" min="0" value={p.amount}
                    onChange={(e) => setAmount(id, e.target.value)}
                    className="input w-32 text-right" />
                </div>

                {/* Only a settled invoice needs a decision. Every other row
                    renders exactly as it did before. */}
                {settled && (
                  <SettledDisposition
                    line={p}
                    onChoose={(annotateOnly) => setDisposition(id, annotateOnly)}
                    onReopened={reopened}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className={`flex items-center justify-between text-sm px-1 ${overAllocated ? 'text-rose-700' : 'text-gray-600'}`}>
        <span>Allocated <span className="font-semibold">{formatCurrency(allocated)}</span></span>
        <span>
          {overAllocated
            ? `Over by ${formatCurrency(Math.abs(remaining))}`
            : `Remaining ${formatCurrency(remaining)}`}
        </span>
      </div>

      {searching && <p className="text-xs text-gray-400 px-1">Searching…</p>}
      {!searching && query.trim().length >= 2 && !results.length && (
        <p className="text-xs text-gray-400 px-1">Nothing matches “{query}”.</p>
      )}

      {truncated > 0 && (
        <p className="text-xs text-amber-700 px-1">
          Showing {results.length}; {truncated} more match — narrow the search.
        </p>
      )}

      {results.length > 0 && (
        <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
          {results.map((inv) => {
            const isPicked = Boolean(picked[inv.id])
            const settled = Number(inv.balanceDue) <= 0
            return (
              // A settled invoice is selectable: the credit may still belong to
              // it, either because the money is already in Books under another
              // record or because whatever closed it was wrong. What happens
              // next is asked once it is picked, not decided by greying it out.
              <button key={inv.id} onClick={() => toggle(inv)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 ${
                  isPicked ? 'bg-brand-50' : 'hover:bg-gray-50'
                }`}>
                <input type="checkbox" readOnly checked={isPicked} className="pointer-events-none" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{inv.number}</span>
                  <span className="text-gray-500"> · {inv.customer?.name}</span>
                </div>
                <span className="text-gray-500 whitespace-nowrap">
                  {settled
                    ? <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-xs font-medium">paid</span>
                    : `owes ${formatCurrency(inv.balanceDue)}`}
                  <span className="text-gray-400"> of {formatCurrency(inv.total)}</span>
                </span>
              </button>
            )
          })}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button onClick={onCancel} className="btn-secondary btn-sm">Cancel</button>
        <button onClick={handleSave} disabled={saving || overAllocated || !Object.keys(picked).length}
          className="btn-primary btn-sm flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5" />
          {saving ? 'Recording…' : allAnnotate
            ? `Link ${count || ''} invoice${count === 1 ? '' : 's'}`
            : `Record ${count || ''} payment${count === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
